import { EditOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Card, Form, message, Space, Tag, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import api from '../../../common/api';
import {
  ERROR_MESSAGE,
  LEVEL,
  MODULES_KEY,
  PERMISSION_OBJECT,
  ROLE_KEYS,
  ROUTES,
  SEARCH_PAGINATION_TABLE_FIELDS,
  SESSION_EXPIRED,
  SYNC_MESSAGE,
  USER,
} from '../../../common/constants';
import { handleDownloadExcelSheet } from '../../../common/utils';
import CommonTable from '../../../components/CommonTable';
import SearchComponent from '../../../components/SearchComponent';
import VerifyPermissions from '../../../components/VerifyPermissions';
import { useTableQueryParams } from '../../../hooks/useTableQueryParams';
import StreamModal from './components/StreamModal';
import { CREATE_STREAM, UPDATE_STREAM } from './graphql/Mutations';
import { STREAMS } from './graphql/Queries';

const buildStreamsFilter = (params) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
  searchString: params?.search,
});

function Streams() {
  // eslint-disable-next-line no-undef
  const user = JSON.parse(localStorage.getItem(USER));
  const history = useHistory();
  const [form] = Form.useForm();
  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const { params, setParams } = useTableQueryParams({
    fields: SEARCH_PAGINATION_TABLE_FIELDS,
  });

  const isCreatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.AOS_PODS,
    allowedPermissions: [PERMISSION_OBJECT?.create],
  });
  const isUpdatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.AOS_PODS,
    allowedPermissions: [PERMISSION_OBJECT?.update],
  });
  const isSendPermission = VerifyPermissions({
    modulekey: MODULES_KEY?.AOS_PODS,
    allowedPermissions: [PERMISSION_OBJECT?.send],
  });

  const [fetchStreams, { loading, data }] = useLazyQuery(STREAMS, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [createStream, { loading: creating }] = useMutation(CREATE_STREAM, {
    onCompleted() {
      setShowModal(false);
      fetchStreams({ variables: { filters: buildStreamsFilter(params) } });
    },
  });

  const [updateStream, { loading: updating }] = useMutation(UPDATE_STREAM, {
    onCompleted() {
      setShowModal(false);
      setEditRecord(null);
      fetchStreams({ variables: { filters: buildStreamsFilter(params) } });
    },
  });

  useEffect(() => {
    fetchStreams({ variables: { filters: buildStreamsFilter(params) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleSearch = (value) => {
    setParams({ search: value?.trim(), page: 1 });
  };

  const handleTableChange = (pagination) => {
    const { current: currentPage, pageSize } = pagination;
    setParams({
      page: pageSize !== params?.pageSize ? 1 : currentPage,
      pageSize,
    });
  };

  const handleOpenCreate = () => {
    setEditRecord(null);
    form.resetFields();
    setShowModal(true);
  };

  const handleDownloadWorkbook = async () => {
    try {
      const response = await api?.get('/api/v1/aos/download-workbook', {
        responseType: 'blob',
      });
      const disposition = response?.headers?.['content-disposition'];
      const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1];
      handleDownloadExcelSheet(response?.data, filename || 'aos-workbook.xlsx');
    } catch (error) {
      let errorMessage = 'Unable to download the workbook.';
      try {
        const text = await error?.response?.data?.text();
        const parsed = JSON.parse(text);
        errorMessage =
          parsed?.message || parsed?.errors?.[0]?.message || errorMessage;
      } catch (parseError) {
        // response body wasn't JSON, fall back to the default message
      }
      message?.destroy();
      message?.error(errorMessage);
      if (error?.response?.status === 401) {
        history?.replace({
          pathname: ROUTES?.AUTHENTICATION,
          // eslint-disable-next-line no-undef
          state: { from: window?.location?.pathname },
        });
      }
    }
  };

  const handleOpenEdit = (record) => {
    setEditRecord(record);
    form.setFieldsValue({
      name: record.name,
      streamLeadId: record.streamLeadId,
      deliveryManagerId: record.deliveryManagerId,
      isActive: record.isActive,
    });
    setShowModal(true);
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditRecord(null);
  };

  const handleSubmit = (values) => {
    if (editRecord) {
      updateStream({
        variables: { where: { id: editRecord.id }, input: values },
      });
    } else {
      createStream({ variables: { input: values } });
    }
  };

  const handleSync = async () => {
    const syncUrls = [
      process.env.REACT_APP_SYNC_STREAMS_URL,
      process.env.REACT_APP_SYNC_PODS_URL,
    ];
    try {
      await Promise?.all(
        syncUrls?.map(async (url) => {
          await api?.post(url);
        }),
      );
      message?.destroy();
      message?.success(SYNC_MESSAGE);
    } catch (error) {
      if (
        error?.response?.data?.errors?.[0]?.extensions?.code === SESSION_EXPIRED
      ) {
        message?.destroy();
        message?.error(ERROR_MESSAGE);
        history?.replace({
          pathname: ROUTES?.AUTHENTICATION,
          // eslint-disable-next-line no-undef
          state: { from: window?.location?.pathname },
        });
      }
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => text,
    },
    {
      title: 'Lead',
      key: 'streamLead',
      render: (_, record) =>
        record.streamLead
          ? `${record.streamLead.firstName} ${record.streamLead.lastName}`
          : '-',
    },
    {
      title: 'Delivery Manager',
      key: 'deliveryManager',
      render: (_, record) =>
        record.deliveryManager
          ? `${record.deliveryManager.firstName} ${record.deliveryManager.lastName}`
          : '-',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (val) => (
        <Tag color={val ? 'green' : 'red'}>{val ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    ...(isUpdatePermission
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
              <Space size="middle">
                <Tooltip title="Edit Stream">
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEdit(record);
                    }}
                  />
                </Tooltip>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="table-card-page">
      <Card
        className="ant-body-scroll"
        title="Streams"
        extra={
          <div className="listing-filter">
            {([ROLE_KEYS?.SUPER_ADMIN, ROLE_KEYS?.ADMIN]?.includes(
              user?.role,
            ) ||
              [LEVEL?.L1, LEVEL?.L2]?.includes(user?.level?.name)) && (
              <Tooltip
                placement="bottom"
                title={<span>Stream, Pods Sync</span>}
              >
                <Button onClick={handleSync}>
                  <SyncOutlined />
                </Button>
              </Tooltip>
            )}
            {isCreatePermission && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenCreate}
              >
                New Stream
              </Button>
            )}{' '}
            {isSendPermission && (
              <Button type="primary" onClick={handleDownloadWorkbook}>
                Export CSV
              </Button>
            )}
            <SearchComponent
              key={params?.search || ''}
              defaultValue={params?.search}
              getData={handleSearch}
              name="Stream"
            />
          </div>
        }
      >
        <div className="card-body-wrapper">
          <CommonTable
            size="small"
            bordered
            columns={columns}
            data={data?.streams?.streams || []}
            loadingData={loading}
            onChange={handleTableChange}
            rowKey={(obj) => obj?.id}
            paginationConfig={{
              current: params?.page,
              total: data?.streams?.count,
              pageSize: params?.pageSize,
            }}
            onRow={(record) => ({
              onClick: () =>
                history.push(`${ROUTES?.AOS_STREAMS}/${record.id}`, {
                  streamName: record.name,
                }),
              style: { cursor: 'pointer' },
            })}
          />
        </div>

        <StreamModal
          form={form}
          open={showModal}
          editRecord={editRecord}
          loading={creating || updating}
          onFinish={handleSubmit}
          onCancel={handleCancel}
        />
      </Card>
    </div>
  );
}

export default Streams;
